import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Media } from "@capacitor-community/media";

const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const getMimeType = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();
  const mimeMap = {
    svg: "image/svg+xml",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    zip: "application/zip",
    dst: "application/octet-stream",
    pes: "application/octet-stream",
    jef: "application/octet-stream",
    exp: "application/octet-stream",
    vp3: "application/octet-stream",
    hus: "application/octet-stream",
    xxx: "application/octet-stream",
  };
  return mimeMap[ext] || "application/octet-stream";
};

/**
 * Downloads a blob as a file. On native iOS/Android uses Capacitor Filesystem
 * to write the file then opens the native share sheet so the user can save it.
 * On web, uses the standard anchor-click pattern.
 */
export async function downloadBlob(blob, filename) {
  if (isNative()) {
    try {
      const base64Data = await blobToBase64(blob);

      const writeResult = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Cache,
        recursive: true,
      });

      await Share.share({
        title: filename,
        url: writeResult.uri,
        dialogTitle: `Save ${filename}`,
      });

      return true;
    } catch (error) {
      console.error("Native download failed:", error);
      webDownload(blob, filename);
      return false;
    }
  } else {
    webDownload(blob, filename);
    return true;
  }
}

function webDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Save a PNG/JPEG blob directly to the device photo library.
 * Only works on native iOS/Android via @capacitor-community/media.
 * Returns true on success, false on failure.
 */
export async function saveToPhotos(blob, filename) {
  if (!isNative()) return false;

  try {
    const base64Data = await blobToBase64(blob);

    const writeResult = await Filesystem.writeFile({
      path: filename,
      data: base64Data,
      directory: Directory.Cache,
      recursive: true,
    });

    await Media.savePhoto({
      path: writeResult.uri,
      albumIdentifier: undefined,
    });

    return true;
  } catch (error) {
    console.error("Save to photos failed:", error);
    return false;
  }
}

export { isNative };
